import { formatOfferSalary } from '@/utils/offerUtils';
import { FIELD_KIND, SECTION_ID } from '@/constants/offer-field-constants';
import type {
  OfferDetailField,
  Currency,
  EquityAward,
  StaticField,
  StaticFieldSection,
  CustomFieldOption,
  CustomField,
  AdditionalInfo,
  FieldWithPosition,
} from './offerDetailsConfig.interface';

const EMPTY_VALUE = '-';

const formatEquityType = (type?: string): string => {
  if (!type) return EMPTY_VALUE;
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatCompensation = (
  amount?: string | number | null,
  currency?: Currency
): string => {
  if (!amount) return EMPTY_VALUE;
  return formatOfferSalary(amount, currency);
};

const formatVestingPeriod = (
  value?: string | number | null,
  unit?: string
): string => {
  if (!value) return EMPTY_VALUE;

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return EMPTY_VALUE;

  const displayUnit = unit ? (numValue > 1 ? `${unit}s` : unit) : '';
  return displayUnit ? `${numValue} ${displayUnit}` : `${numValue}`;
};

const formatDateFromTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatRelTagValue = (
  value: number | number[],
  options?: CustomFieldOption[]
): string => {
  if (Array.isArray(value)) {
    if (value.length === 0) return EMPTY_VALUE;

    const selectedValues = value
      .map((id) => options?.find((option) => option.id === id)?.value)
      .filter(Boolean);

    return selectedValues.length > 0 ? selectedValues.join(', ') : EMPTY_VALUE;
  }

  const selectedValue = options?.find((option) => option.id === value)?.value;
  return selectedValue || EMPTY_VALUE;
};

const isHtmlContent = (value: string): boolean => {
  if (!value) return false;
  const htmlTagPattern = /<[^>]+>/;
  return htmlTagPattern.test(value);
};

// File custom fields store an object ({ url, filename }) — or an array of them —
// not a string. Render the filename as a download link so it doesn't fall to the
// default String(value) and show "[object Object]". The value is rendered via
// DOMPurify-sanitized innerHTML, so an anchor is safe.
const formatRelFileValue = (value: any): string => {
  const toLink = (file: any): string | null => {
    if (!file || typeof file !== 'object') return null;
    const name = file.filename || file.name;
    if (!name && !file.url) return null;
    if (file.url) {
      return `<a href="${file.url}" target="_blank" rel="noopener noreferrer">${
        name || file.url
      }</a>`;
    }
    return name;
  };

  const links = (Array.isArray(value) ? value : [value])
    .map(toLink)
    .filter(Boolean);
  return links.length ? links.join(', ') : EMPTY_VALUE;
};

const formatCustomFieldValue = (
  value: any,
  kind: string,
  options?: CustomFieldOption[]
): string => {
  if (!value && value !== 0) return EMPTY_VALUE;

  switch (kind) {
    case 'rel_file':
      return formatRelFileValue(value);

    case 'date':
      return typeof value === 'number'
        ? formatDateFromTimestamp(value)
        : value || EMPTY_VALUE;

    case 'rel_tag':
      return formatRelTagValue(value, options);

    // rel_user ("Users") — value is an array of user cards { id, name, avatar }.
    case 'rel_user':
      return Array.isArray(value)
        ? value
            .map((user: { name?: string }) => user?.name)
            .filter(Boolean)
            .join(', ') || EMPTY_VALUE
        : String(value) || EMPTY_VALUE;

    case 'enum_boolean': {
      const str = String(value);
      if (str === 'Y') return 'Yes';
      if (str === 'N') return 'No';
      return str || EMPTY_VALUE;
    }

    case 'string':
    case 'text':
    case 'email':
    case 'phone':
    case 'integer':
    case 'decimal':
    case 'number': {
      const stringValue = String(value);
      if (isHtmlContent(stringValue)) {
        return stringValue;
      }
      return stringValue || EMPTY_VALUE;
    }

    default: {
      const defaultValue = String(value);
      if (isHtmlContent(defaultValue)) {
        return defaultValue;
      }
      return defaultValue || EMPTY_VALUE;
    }
  }
};
const buildCompensationFields = (
  currentOffer: any
): Record<string, OfferDetailField> => {
  const {
    salary,
    salary_currency,
    salary_frequency,
    additional_compensation,
    additional_compensation_currency,
  } = currentOffer || {};

  return {
    salary: {
      variable: 'salary',
      id: 'salary',
      label: 'Salary',
      value: formatOfferSalary(salary, salary_currency),
    },
    pay_type: {
      variable: 'pay_type',
      id: 'payType',
      label: 'Pay type',
      value: salary_frequency || EMPTY_VALUE,
    },
    additional_bonus: {
      variable: 'additional_bonus',
      id: 'additionalBonus',
      label: 'Additional bonus (one-time)',
      value: formatCompensation(
        additional_compensation,
        additional_compensation_currency
      ),
    },
  };
};

const buildCandidateFields = (
  currentOffer: any
): Record<string, OfferDetailField> => {
  const {
    candidate_name,
    candidate_email,
    candidate_phone,
    candidate_address,
  } = currentOffer || {};

  return {
    candidate_name: {
      variable: 'candidate_name',
      id: 'candidateName',
      label: 'Name',
      value: candidate_name || EMPTY_VALUE,
    },
    candidate_email: {
      variable: 'candidate_email',
      id: 'candidateEmail',
      label: 'Email',
      value: candidate_email || EMPTY_VALUE,
    },
    candidate_phone: {
      variable: 'candidate_phone',
      id: 'candidatePhone',
      label: 'Phone',
      value: candidate_phone || EMPTY_VALUE,
    },
    candidate_address: {
      variable: 'candidate_address',
      id: 'candidateAddress',
      label: 'Address',
      value: candidate_address || EMPTY_VALUE,
    },
  };
};

const buildJobFields = (
  currentOffer: any
): Record<string, OfferDetailField> => {
  const { job_title, req_id, department, office, reporting_manager } =
    currentOffer || {};

  return {
    job_title: {
      variable: 'job_title',
      id: 'jobTitle',
      label: 'Job title',
      value: job_title || EMPTY_VALUE,
    },
    req_id: {
      variable: 'req_id',
      id: 'requisitionId',
      label: 'Requisition ID',
      value: req_id || EMPTY_VALUE,
    },
    department: {
      variable: 'department',
      id: 'department',
      label: 'Department',
      value: department?.name || EMPTY_VALUE,
    },
    office: {
      variable: 'office',
      id: 'office',
      label: 'Office',
      value: office?.name || EMPTY_VALUE,
    },
    reporting_manager: {
      variable: 'reporting_manager',
      id: 'reportingManager',
      label: 'Reporting manager',
      value: reporting_manager?.name || EMPTY_VALUE,
    },
  };
};

const buildDateFields = (
  currentOffer: any
): Record<string, OfferDetailField> => {
  const { today_date, start_date, expiration_date } = currentOffer || {};

  return {
    start_date: {
      variable: 'start_date',
      id: 'startDate',
      label: 'Start date',
      value: start_date || EMPTY_VALUE,
    },
    offer_date: {
      variable: 'offer_date',
      id: 'offerDate',
      label: 'Offer date',
      value: today_date || EMPTY_VALUE,
    },
    expiration_date: {
      variable: 'expiration_date',
      id: 'expirationDate',
      label: 'Offer expiration date',
      value: expiration_date || EMPTY_VALUE,
    },
  };
};

const buildEquityFields = (
  equityAward?: EquityAward
): Record<string, OfferDetailField> => {
  return {
    equity_type: {
      variable: 'equity_type',
      id: 'equityType',
      label: 'Type of equity',
      value: formatEquityType(equityAward?.type_of_equity),
    },
    stock_award_type: {
      variable: 'stock_award_type',
      id: 'stockAwardType',
      label: 'Stock award type',
      value: 'Units',
    },
    equity_amount: {
      variable: 'equity_amount',
      id: 'equityAmount',
      label: 'Equity units/amount',
      value:
        equityAward?.amount?.split('.')?.[0] ||
        equityAward?.amount ||
        EMPTY_VALUE,
    },
    equity_cliff: {
      variable: 'equity_cliff',
      id: 'equityCliff',
      label: 'Equity cliff',
      value: formatVestingPeriod(
        equityAward?.cliff_of_vesting,
        equityAward?.unit_of_cliff
      ),
    },
    equity_post_cliff_vesting_cycle: {
      variable: 'equity_post_cliff_vesting_cycle',
      id: 'postCliffVesting',
      label: 'Post-cliff vesting period',
      value: formatVestingPeriod(
        equityAward?.cycles_after_cliff,
        equityAward?.unit_of_cycle_after_cliff
      ),
    },
    equity_vesting_period: {
      variable: 'equity_vesting_period',
      id: 'vestingPeriod',
      label: 'Vesting period',
      value: formatVestingPeriod(
        equityAward?.period_of_vesting,
        equityAward?.unit_of_vesting_period
      ),
    },
    equity_strike_price: {
      variable: 'equity_strike_price',
      id: 'strikePrice',
      label: 'Equity strike price',
      value: formatCompensation(
        equityAward?.strike_price,
        equityAward?.strike_price_currency
      ),
    },
    date_of_grant: {
      variable: 'date_of_grant',
      id: 'dateOfGrant',
      label: 'Date of grant',
      value: equityAward?.date_of_grant || EMPTY_VALUE,
    },
    start_date_of_vesting: {
      variable: 'start_date_of_vesting',
      id: 'startDateOfVesting',
      label: 'Start date of vesting',
      value: equityAward?.start_date_of_vesting || EMPTY_VALUE,
    },
  };
};

const buildStaticFieldsMap = (
  currentOffer: any,
  equityAward?: EquityAward
): Record<string, OfferDetailField> => {
  return {
    ...buildCompensationFields(currentOffer),
    ...buildCandidateFields(currentOffer),
    ...buildJobFields(currentOffer),
    ...buildDateFields(currentOffer),
    ...buildEquityFields(equityAward),
  };
};

// Walks sections in BE order; under flag-on customs are embedded in static section fields[].
const processSectionFields = (
  staticFieldSections: StaticFieldSection[],
  fieldConfigsMap: Record<string, OfferDetailField>,
  additionalInfo: AdditionalInfo[]
): { fields: FieldWithPosition[]; renderedRowIds: Set<string> } => {
  const fields: FieldWithPosition[] = [];
  const renderedRowIds = new Set<string>();
  let position = 0;

  staticFieldSections.forEach((section) => {
    if ('enabled' in section && section.enabled === false) return;

    section?.fields?.forEach((field) => {
      if (isCustomFieldEntry(field)) {
        const entry = buildCustomFieldFromSectionEntry(field, additionalInfo);
        if (!entry) return;
        entry.position = position++;
        fields.push(entry);
        const rowId = field?.id;
        if (rowId !== undefined && rowId !== null)
          renderedRowIds.add(String(rowId));
        return;
      }

      const variable = field?.variable;
      const fieldConfig = variable ? fieldConfigsMap[variable] : null;
      if (fieldConfig) {
        fields.push({
          field,
          fieldConfig,
          position: position++,
          isCustom: false,
        });
      }
    });
  });

  return { fields, renderedRowIds };
};

const isCustomFieldEntry = (field: any): boolean =>
  field?.field_kind === FIELD_KIND.CUSTOM ||
  (field?.ats_custom_field_id !== undefined &&
    field?.ats_custom_field_id !== null);

const buildCustomFieldFromSectionEntry = (
  entry: any,
  additionalInfo: AdditionalInfo[]
): FieldWithPosition | null => {
  const rowId = entry?.id;
  if (rowId === undefined || rowId === null) return null;

  const fieldType = entry?.kind ?? entry?.type;
  const value = getCustomFieldValue(rowId, additionalInfo);
  const formattedValue = formatCustomFieldValue(
    value,
    fieldType,
    entry?.values
  );

  return {
    field: {
      variable: entry?.variable,
      hidden: false,
      label: entry?.label,
      description: entry?.description,
    },
    fieldConfig: {
      variable: entry?.variable,
      id: String(rowId),
      label: entry?.label,
      value: formattedValue,
      description: entry?.description,
    },
    position: 0,
    isCustom: true,
  };
};

const getCustomFieldValue = (
  fieldId: number | string,
  additionalInfo: AdditionalInfo[]
): any => {
  const key = String(fieldId);
  const item = additionalInfo.find((entry) => String(entry.field_id) === key);
  // Prefer ext_data — BE stores the verbatim user string there for number
  // fields so EU separator inputs like "1.234.567" survive Decimal storage.
  return item?.ext_data ?? item?.value;
};

const createCustomFieldConfig = (
  customField: CustomField,
  value: any
): FieldWithPosition => {
  const { id, name, label, kind, position, values, description } = customField;
  const formattedValue = formatCustomFieldValue(value, kind, values);

  return {
    field: {
      variable: name,
      hidden: false,
      label,
      description,
    },
    fieldConfig: {
      variable: name,
      id: String(id),
      label,
      value: formattedValue,
      description,
    },
    position: position ?? 0,
    isCustom: true,
  };
};

// Flag-off fallback: customs aren't embedded in sections, append from flat list. No-op under flag-on (already rendered).
const processCustomFields = (
  customFields: CustomField[],
  additionalInfo: AdditionalInfo[],
  fallbackStartPosition: number,
  renderedRowIds: Set<string>
): FieldWithPosition[] => {
  const result: FieldWithPosition[] = [];
  let fallbackPosition = fallbackStartPosition;

  customFields.forEach((customField) => {
    const { id, name } = customField;
    if (!id || !name) return;
    if (renderedRowIds.has(String(id))) return;
    if (customField?.field_kind === FIELD_KIND.STATIC) return;

    const value = getCustomFieldValue(id, additionalInfo);
    const fieldConfig = createCustomFieldConfig(customField, value);

    fieldConfig.position = fieldConfig.position || fallbackPosition++;
    result.push(fieldConfig);
  });

  return result;
};

const shouldShowField = (
  fieldConfig: OfferDetailField,
  field: StaticField,
  isCustom: boolean
): boolean => {
  const hasValue =
    fieldConfig.value && fieldConfig.value.trim() !== EMPTY_VALUE;

  if (isCustom) return hasValue;
  return !field.hidden && hasValue;
};

const combineAndSortFields = (
  staticFields: FieldWithPosition[],
  customFields: FieldWithPosition[]
): FieldWithPosition[] => {
  return [...staticFields, ...customFields].sort(
    (a, b) => a.position - b.position
  );
};

export const getOfferDetailsConfig = (
  currentOffer: any,
  staticFieldSections: StaticFieldSection[] = [],
  customFields: CustomField[] = []
): OfferDetailField[] => {
  const equityAward = currentOffer?.ats_equity_award;
  const additionalInfo = currentOffer?.additional_info || [];

  const fieldConfigsMap = buildStaticFieldsMap(currentOffer, equityAward);

  const { fields: sectionFields, renderedRowIds } = processSectionFields(
    staticFieldSections,
    fieldConfigsMap,
    additionalInfo
  );

  const fallbackCustomFields = processCustomFields(
    customFields,
    additionalInfo,
    sectionFields.length,
    renderedRowIds
  );

  return combineAndSortFields(sectionFields, fallbackCustomFields)
    .filter(({ field, fieldConfig, isCustom }) =>
      shouldShowField(fieldConfig, field, isCustom)
    )
    .map(({ fieldConfig }) => fieldConfig);
};

export interface OfferDetailSection {
  sectionId: string;
  sectionLabel: string;
  fields: OfferDetailField[];
}

// Same field building + settings order as getOfferDetailsConfig, but grouped by
// section (notification-style layout for the approval modal). Skips toggled-off
// sections and hidden/empty fields; drops empty sections.
export const getSectionedOfferDetails = (
  currentOffer: any,
  staticFieldSections: StaticFieldSection[] = [],
  customFields: CustomField[] = []
): OfferDetailSection[] => {
  const equityAward = currentOffer?.ats_equity_award;
  const additionalInfo = currentOffer?.additional_info || [];
  const fieldConfigsMap = buildStaticFieldsMap(currentOffer, equityAward);

  const sections: OfferDetailSection[] = [];
  const renderedRowIds = new Set<string>();

  staticFieldSections.forEach((section) => {
    if ('enabled' in section && section.enabled === false) return;
    const fields: OfferDetailField[] = [];

    section?.fields?.forEach((field) => {
      if (isCustomFieldEntry(field)) {
        const entry = buildCustomFieldFromSectionEntry(field, additionalInfo);
        const rowId = field?.id;
        if (rowId !== undefined && rowId !== null)
          renderedRowIds.add(String(rowId));
        if (entry && shouldShowField(entry.fieldConfig, entry.field, true))
          fields.push(entry.fieldConfig);
        return;
      }
      const variable = field?.variable;
      const fieldConfig = variable ? fieldConfigsMap[variable] : null;
      if (fieldConfig && shouldShowField(fieldConfig, field, false))
        fields.push(fieldConfig);
    });

    if (fields.length)
      sections.push({
        sectionId: String(section?.id || ''),
        sectionLabel: section?.label || '',
        fields,
      });
  });

  // Flag-off fallback: customs not embedded in sections get their own group.
  const fallback = processCustomFields(
    customFields,
    additionalInfo,
    0,
    renderedRowIds
  )
    .filter(({ field, fieldConfig, isCustom }) =>
      shouldShowField(fieldConfig, field, isCustom)
    )
    .map(({ fieldConfig }) => fieldConfig);

  if (fallback.length)
    sections.push({
      sectionId: SECTION_ID.CUSTOM,
      sectionLabel: 'Additional details',
      fields: fallback,
    });

  return sections;
};

// Whether the offer has any document to show in the "Offer documents" section:
// a generated single or multi-doc PDF, the DOCX, a signed PDF, or an additional
// uploaded attachment. offer_documents_pdf (multi-doc) must be included or a
// multi-doc offer with no single PDF hides the section despite having documents.
export const offerHasDocuments = (offer?: {
  offer_document_pdf?: { url?: string };
  offer_documents_pdf?: unknown[];
  offer_document_docx?: { url?: string };
  signed_offer_document_pdf?: { url?: string };
  attachments?: unknown[];
}): boolean =>
  !!(
    offer?.offer_document_pdf?.url ||
    offer?.offer_documents_pdf?.length ||
    offer?.offer_document_docx?.url ||
    offer?.signed_offer_document_pdf?.url ||
    offer?.attachments?.length
  );
